import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";

type DailyStat = {
  date: string;
  guest: number;
  user: number;
  bot: number;
  unique_users: number;
  unique_guests: number;
  page_views: number;
};

type CountRow<K extends string> = { [P in K]: string } & { count: number };

interface AnalyticsResponse {
  range: { from: string; to: string; days: number };
  totals: {
    total_visits: number;
    guest_visits: number;
    user_visits: number;
    bot_visits: number;
    unique_users: number;
    unique_guests: number;
    total_page_views: number;
    avg_views_per_visit: number;
  };
  breakdown: Record<string, number>;
  daily: DailyStat[];
  top_landing_pages: CountRow<"path">[];
  top_exit_pages: CountRow<"path">[];
  top_pages: CountRow<"path">[];
  top_referrers: CountRow<"referrer">[];
  top_bots: CountRow<"bot">[];
  browsers: { key: string; count: number }[];
  os: { key: string; count: number }[];
  devices: { key: string; count: number }[];
  recent_visits: {
    id: number;
    visitor_type: "guest" | "user" | "bot";
    bot_name: string | null;
    user_id: number | null;
    landing_path: string | null;
    exit_path: string | null;
    views_count: number;
    browser: string | null;
    os: string | null;
    device: string | null;
    referrer: string | null;
    ip_address: string | null;
    started_at: string;
    last_seen_at: string;
  }[];
}

function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
}

function StatCard({
  label,
  value,
  sub,
  color = "gray",
}: {
  label: string;
  value: number | string;
  sub?: string;
  color?: "gray" | "blue" | "green" | "red" | "yellow" | "purple";
}) {
  const colors: Record<string, string> = {
    gray: "border-gray-200 bg-white",
    blue: "border-blue-200 bg-blue-50",
    green: "border-green-200 bg-green-50",
    red: "border-red-200 bg-red-50",
    yellow: "border-yellow-200 bg-yellow-50",
    purple: "border-purple-200 bg-purple-50",
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <p className="text-sm text-gray-600">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="mt-1 text-xs text-gray-500">{sub}</p>}
    </div>
  );
}

function StackedBarChart({ data }: { data: DailyStat[] }) {
  const max = Math.max(...data.map((d) => d.guest + d.user + d.bot), 1);
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <h3 className="font-semibold text-gray-900">Apsilankymai per dieną</h3>
      <div className="mt-4 space-y-1">
        {data.map((d) => {
          const total = d.guest + d.user + d.bot;
          const pct = (n: number) => (n / max) * 100;
          return (
            <div key={d.date} className="flex items-center gap-3 text-xs">
              <span className="w-20 shrink-0 text-gray-500">
                {new Date(d.date).toLocaleDateString("lt-LT", {
                  month: "short",
                  day: "numeric",
                })}
              </span>
              <div className="flex h-5 flex-1 overflow-hidden rounded bg-gray-50">
                <div className="bg-blue-500" style={{ width: `${pct(d.user)}%` }} title={`Naudotojai: ${d.user}`} />
                <div className="bg-gray-400" style={{ width: `${pct(d.guest)}%` }} title={`Svečiai: ${d.guest}`} />
                <div className="bg-yellow-500" style={{ width: `${pct(d.bot)}%` }} title={`Botai: ${d.bot}`} />
              </div>
              <span className="w-10 text-right font-medium text-gray-900">{total}</span>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex gap-4 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1">
          <span className="h-3 w-3 rounded bg-blue-500" /> Naudotojai
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-3 w-3 rounded bg-gray-400" /> Svečiai
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-3 w-3 rounded bg-yellow-500" /> Botai
        </span>
      </div>
    </div>
  );
}

function UniqueVisitorsChart({ data }: { data: DailyStat[] }) {
  const max = Math.max(...data.map((d) => d.unique_users + d.unique_guests), 1);
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <h3 className="font-semibold text-gray-900">Unikalūs lankytojai per dieną</h3>
      <p className="mt-1 text-xs text-gray-500">
        Unikalūs naudotojai (pagal paskyrą) ir svečiai (pagal sesiją)
      </p>
      <div className="mt-4 space-y-1">
        {data.map((d) => {
          const total = d.unique_users + d.unique_guests;
          const pct = (n: number) => (n / max) * 100;
          return (
            <div key={d.date} className="flex items-center gap-3 text-xs">
              <span className="w-20 shrink-0 text-gray-500">
                {new Date(d.date).toLocaleDateString("lt-LT", {
                  month: "short",
                  day: "numeric",
                })}
              </span>
              <div className="flex h-5 flex-1 overflow-hidden rounded bg-gray-50">
                <div
                  className="bg-blue-500"
                  style={{ width: `${pct(d.unique_users)}%` }}
                  title={`Unikalūs naudotojai: ${d.unique_users}`}
                />
                <div
                  className="bg-gray-400"
                  style={{ width: `${pct(d.unique_guests)}%` }}
                  title={`Unikalūs svečiai: ${d.unique_guests}`}
                />
              </div>
              <span className="w-16 text-right text-gray-500">
                <span className="font-medium text-blue-600">{d.unique_users}</span>
                {" / "}
                <span className="font-medium text-gray-700">{d.unique_guests}</span>
              </span>
              <span className="w-10 text-right font-medium text-gray-900">{total}</span>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex gap-4 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1">
          <span className="h-3 w-3 rounded bg-blue-500" /> Unikalūs naudotojai
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-3 w-3 rounded bg-gray-400" /> Unikalūs svečiai
        </span>
      </div>
    </div>
  );
}

function TopList({
  title,
  rows,
  labelKey,
  empty = "Nėra duomenų",
}: {
  title: string;
  rows: { count: number; [k: string]: string | number | null }[];
  labelKey: string;
  empty?: string;
}) {
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <h3 className="font-semibold text-gray-900">{title}</h3>
      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-gray-500">{empty}</p>
      ) : (
        <div className="mt-3 space-y-1">
          {rows.map((row, i) => {
            const label = String(row[labelKey] ?? "—");
            return (
              <div key={`${label}-${i}`} className="flex items-center gap-3 text-sm">
                <span className="min-w-0 flex-1 truncate text-gray-700" title={label}>
                  {label}
                </span>
                <div className="h-4 w-24 overflow-hidden rounded bg-gray-50">
                  <div
                    className="h-full bg-primary-600"
                    style={{ width: `${(row.count / max) * 100}%` }}
                  />
                </div>
                <span className="w-10 text-right font-medium text-gray-900">
                  {row.count}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function VisitorBadge({ type, botName }: { type: string; botName?: string | null }) {
  const colors: Record<string, string> = {
    user: "bg-blue-100 text-blue-800",
    guest: "bg-gray-100 text-gray-700",
    bot: "bg-yellow-100 text-yellow-800",
  };
  const label = type === "bot" && botName ? botName : type;
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${colors[type] || colors.guest}`}>
      {label}
    </span>
  );
}

type ChartMode = "unique" | "total";

function ChartModeToggle({
  mode,
  onChange,
}: {
  mode: ChartMode;
  onChange: (mode: ChartMode) => void;
}) {
  const options: { value: ChartMode; label: string }[] = [
    { value: "unique", label: "Unikalūs lankytojai" },
    { value: "total", label: "Iš viso apsilankymų" },
  ];
  return (
    <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 text-sm">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`rounded-md px-3 py-1.5 font-medium transition ${
            mode === opt.value
              ? "bg-primary-600 text-white shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function AdminAnalyticsPage() {
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [chartMode, setChartMode] = useState<ChartMode>("unique");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`/admin/analytics?days=${days}`);
      const json = await res.json();
      setData(json);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatDate = (iso: string) => new Date(iso).toLocaleString("lt-LT");

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <Link
        to="/admin"
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        ← Admin valdymo skydas
      </Link>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Analitika</h1>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="input w-40"
        >
          <option value={1}>Paskutinės 24 val.</option>
          <option value={7}>Paskutinės 7 dienos</option>
          <option value={30}>Paskutinės 30 dienų</option>
          <option value={90}>Paskutinės 90 dienų</option>
          <option value={365}>Paskutiniai metai</option>
        </select>
      </div>

      {loading || !data ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-6">
            <StatCard
              label="Iš viso apsilankymų"
              value={data.totals.total_visits}
              sub={`${data.totals.avg_views_per_visit} vid. peržiūrų`}
            />
            <StatCard
              label="Naudotojai"
              value={data.totals.user_visits}
              sub={`${data.totals.unique_users} unikalūs`}
              color="blue"
            />
            <StatCard
              label="Svečiai"
              value={data.totals.guest_visits}
              sub={`${data.totals.unique_guests} unikalūs`}
              color="gray"
            />
            <StatCard
              label="Botai"
              value={data.totals.bot_visits}
              color="yellow"
            />
            <StatCard
              label="Puslapių peržiūros"
              value={data.totals.total_page_views}
              color="green"
            />
            <StatCard
              label="Laikotarpis"
              value={`${data.range.days} d.`}
              sub={new Date(data.range.from).toLocaleDateString("lt-LT")}
            />
          </div>

          <div className="mt-8 flex justify-end">
            <ChartModeToggle mode={chartMode} onChange={setChartMode} />
          </div>
          <div className="mt-3">
            {chartMode === "unique" ? (
              <UniqueVisitorsChart data={data.daily} />
            ) : (
              <StackedBarChart data={data.daily} />
            )}
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <TopList
              title="Pagrindiniai patekimo puslapiai"
              rows={data.top_landing_pages}
              labelKey="path"
            />
            <TopList
              title="Pagrindiniai išėjimo puslapiai"
              rows={data.top_exit_pages}
              labelKey="path"
            />
            <TopList
              title="Populiariausi puslapiai"
              rows={data.top_pages}
              labelKey="path"
            />
            <TopList
              title="Nukreipimai"
              rows={data.top_referrers}
              labelKey="referrer"
            />
            <TopList
              title="Botai"
              rows={data.top_bots}
              labelKey="bot"
            />
            <TopList
              title="Naršyklės"
              rows={data.browsers.map((b) => ({ browser: b.key, count: b.count }))}
              labelKey="browser"
            />
            <TopList
              title="Operacinės sistemos"
              rows={data.os.map((o) => ({ os: o.key, count: o.count }))}
              labelKey="os"
            />
            <TopList
              title="Įrenginiai"
              rows={data.devices.map((d) => ({ device: d.key, count: d.count }))}
              labelKey="device"
            />
          </div>

          <h2 className="mt-10 text-lg font-semibold text-gray-900">
            Paskutiniai apsilankymai
          </h2>
          <div className="mt-4 overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Tipas
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Patekimas
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Išėjimas
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Peržiūros
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Naršyklė / OS
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Pradžia
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.recent_visits.map((visit) => (
                  <tr key={visit.id}>
                    <td className="whitespace-nowrap px-3 py-2">
                      <VisitorBadge type={visit.visitor_type} botName={visit.bot_name} />
                    </td>
                    <td className="max-w-xs truncate px-3 py-2 text-sm text-gray-700" title={visit.landing_path || ""}>
                      {visit.landing_path || "—"}
                    </td>
                    <td className="max-w-xs truncate px-3 py-2 text-sm text-gray-700" title={visit.exit_path || ""}>
                      {visit.exit_path || "—"}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-sm font-medium text-gray-900">
                      {visit.views_count}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-600">
                      {[visit.browser, visit.os, visit.device].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-600">
                      {formatDate(visit.started_at)}
                    </td>
                  </tr>
                ))}
                {data.recent_visits.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-sm text-gray-500">
                      Apsilankymų nerasta
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
